// ---------------------------------------------------------------------------
// HDDN LAB marketing site — data access.
//
// The marketing pages read the same sources the booking app already reads, so
// there is exactly one place to edit anything:
//   /api/packages-config  ->  hourly rate, podcast packages, gear, add-ons
//   /api/site-content     ->  copy, rates, retainer, case-study figures
//   /api/photos           ->  space photography and portfolio work
//
// The gear derivation below deliberately mirrors hddn-lab-booking.jsx:1501-1546
// line for line, including the `showUpstairsEquipment` flag and the
// `enabled !== false` package filter. If the accordion hides something, this
// page hides it too.
// ---------------------------------------------------------------------------

import { useEffect, useState, useMemo } from 'react';
import {
  ALA_CARTE, CASE_STUDY, GETTING_HERE, GOOD_TO_KNOW, REPORTING, RETAINER,
  SITE_COPY, STUDIO_RATES, STUDIO_SPECS, STUDIO_USES,
} from './content.js';

const API_BASE = import.meta.env.VITE_API_URL || '';

// One fetch per endpoint per page load, shared by every marketing page. Without
// this each client-side navigation refetched all three, which is both wasteful
// and enough traffic to matter against the API's rate limit.
const inflight = new Map();

function getJson(path) {
  if (inflight.has(path)) return inflight.get(path);
  const req = fetch(`${API_BASE}${path}`)
    .then((r) => (r.ok ? r.json() : null))
    .catch(() => null);
  inflight.set(path, req);
  return req;
}

// Shallow merge that treats an empty admin value as "not set", so clearing a
// field in the editor falls back to the default rather than blanking the page.
function overlay(base, patch) {
  if (!patch || typeof patch !== 'object') return base;
  if (Array.isArray(patch)) return patch.length ? patch : base;
  const out = { ...base };
  for (const [k, v] of Object.entries(patch)) {
    if (v === null || v === undefined || v === '') continue;
    if (Array.isArray(v)) out[k] = v.length ? v : base[k];
    else if (typeof v === 'object' && !Array.isArray(base[k])) out[k] = overlay(base[k] || {}, v);
    else out[k] = v;
  }
  return out;
}

// The Paws & Portraits collection is not uploaded photos: it is every
// published hero shot from the paws gallery, read live from the same rows the
// gallery serves, so it never drifts and nobody has to upload anything. Each
// item is shaped like a photo row so the collections view treats it as one,
// plus a `link` to the pet's own page.
export const PAWS_COLLECTION = 'Paws & Portraits';

function pawsAsPhotos(entries) {
  if (!Array.isArray(entries)) return [];
  return entries
    .filter((e) => e && e.slug && e.heroUrl)
    .map((e, i) => ({
      id: `paws-${e.slug}`,
      url: `${API_BASE}${e.heroUrl}?w=1200`,
      caption: e.petName || 'Paws & Portraits',
      sort_order: i,
      room: 'all',
      event: PAWS_COLLECTION,
      video_url: null,
      link: e.pageUrl || null,
    }));
}

// Event galleries (State of the City portraits and the like) are the same
// idea: each published gallery is a folder named after the event, every
// portrait an item that links to its own page.
function eventsAsPhotos(galleries) {
  if (!Array.isArray(galleries)) return [];
  return galleries.flatMap((g) => (Array.isArray(g.photos) ? g.photos : [])
    .filter((e) => e && e.slug && e.heroUrl)
    .map((e, i) => ({
      id: `event-${g.slug}-${e.slug}`,
      url: `${API_BASE}${e.heroUrl}?w=1200`,
      caption: e.name || g.title,
      sort_order: i,
      room: 'all',
      event: g.title,
      video_url: null,
      link: e.pageUrl || null,
    })));
}

export function useSiteData() {
  const [siteContent, setSiteContent] = useState(null);
  const [packages, setPackages] = useState(null);
  const [photos, setPhotos] = useState(null);

  useEffect(() => {
    let live = true;
    Promise.all([
      getJson('/api/site-content'),
      getJson('/api/packages-config'),
      getJson('/api/photos'),
      getJson('/api/paws-gallery/portfolio'),
      getJson('/api/event-galleries'),
    ]).then(([sc, pk, ph, paws, events]) => {
      if (!live) return;
      if (sc && typeof sc === 'object') setSiteContent(sc);
      if (pk && typeof pk === 'object') setPackages(pk);
      // Uploaded photos tagged with the same name sit in the same folder,
      // ahead of the live entries, so the owner can pin a cover if they want.
      setPhotos([...(Array.isArray(ph) ? ph : []), ...pawsAsPhotos(paws), ...eventsAsPhotos(events)]);
    });
    return () => { live = false; };
  }, []);

  return useMemo(() => {
    const sc = siteContent || {};
    const pk = packages || {};

    const copy = overlay(SITE_COPY, sc.site_copy);
    const caseStudy = overlay(CASE_STUDY, sc.case_study);
    const retainer = overlay(RETAINER, sc.retainer);
    const alaCarte = overlay(ALA_CARTE, sc.ala_carte);
    const specs = overlay(STUDIO_SPECS, sc.studio_specs);

    // Hourly rate is owned by packages_config; the longer blocks by site_content.
    const hourlyRate = Number(pk.hourlyRate) || 100;
    const blockRates = Array.isArray(sc.studio_rates) && sc.studio_rates.length
      ? sc.studio_rates
      : STUDIO_RATES.map((r) => (r.id === 'hourly'
        ? { ...r, price: hourlyRate, foot: `$${hourlyRate * 3} minimum` }
        : r));

    return {
      loading: photos === null,
      copy,
      caseStudy,
      retainer,
      alaCarte,
      specs,
      blockRates,
      hourlyRate,
      reporting: Array.isArray(sc.reporting) && sc.reporting.length ? sc.reporting : REPORTING,
      gettingHere: overlay(GETTING_HERE, sc.getting_here),
      goodToKnow: Array.isArray(sc.good_to_know) && sc.good_to_know.length ? sc.good_to_know : GOOD_TO_KNOW,
      uses: STUDIO_USES,
      studioGear: deriveStudioGear(pk),
      podcastPackages: derivePodcastPackages(pk),
      podcastGear: derivePodcastGear(pk),
      studioAddOns: Array.isArray(pk.studioAddOns) ? pk.studioAddOns : [],
      podcastAddOns: Array.isArray(pk.podcastAddOns) ? pk.podcastAddOns : [],
      photos: photos || [],
    };
  }, [siteContent, packages, photos]);
}

// ---- gear ----------------------------------------------------------------
// Mirrors the "What's Included" derivation in the booking app. The lighting,
// modifiers and grip sections stay hidden unless `showUpstairsEquipment` is on,
// exactly as they do there — nothing appears publicly that the owner has chosen
// to hide.
//
// The one departure: paid add-ons are left out here, because the Studio page
// renders them as a priced table right below. Listing them twice made the page
// read as though the gear came free and then charged for it.
export function deriveStudioGear(pk) {
  const showEquipment = pk.showUpstairsEquipment === true;
  return [
    {
      heading: 'The space',
      items: [
        'Open floor plan with hardwood floors',
        'Natural lighting + blackout options',
        'Furniture & props for styling',
        'WiFi & Bluetooth speaker',
        'Climate controlled space',
        'Spring clamps',
        'V-flat',
        'Clothing rack',
      ],
    },
    ...(showEquipment ? [
      { heading: 'Lighting', items: ['Amaran 200x (bi-color, warm/cool)', 'Aputure 120D (daylight)'] },
      { heading: 'Modifiers', items: ['36" Lantern dome', '30x120 Lightbox', '36" Aputure dome', 'Snoot'] },
      { heading: 'Grip & support', items: ['4 x 40" C-stands', '6 x 25lb sandbags', 'Extension cords'] },
    ] : []),
    {
      heading: 'Backdrops',
      items: [
        'White backdrop (no stepping — $25 stepping fee)',
        'Black backdrop (no stepping — $25 stepping fee)',
      ],
    },
    { heading: 'Amenities', items: ['MUA station', 'Props on site'] },
  ];
}

export function derivePodcastPackages(pk) {
  const pkgs = pk.podcastPackages || {};
  return Object.entries(pkgs)
    .filter(([, p]) => p && p.enabled !== false)
    .map(([id, p]) => ({
      id,
      name: p.name || 'Package',
      tagline: p.tagline || '',
      description: p.description || '',
      durations: Array.isArray(p.durations) ? p.durations : [],
      includes: Array.isArray(p.includes) ? p.includes : [],
    }));
}

export function derivePodcastGear(pk) {
  const gear = Array.isArray(pk.podcastGear) ? pk.podcastGear.filter((s) => s && s.trim()) : [];
  const turnaround = typeof pk.podcastTurnaround === 'string' ? pk.podcastTurnaround.trim() : '';
  return { gear, turnaround };
}

// ---- photos --------------------------------------------------------------
// Three distinct pools, split the same way PhotoGallery already splits them
// (hddn-lab-booking.jsx:2311-2325):
//   untagged + room  -> the space itself
//   event="about"    -> "Inside the Studio"
//   any other event  -> portfolio work
export function spacePhotos(photos, room) {
  const space = (photos || []).filter((p) => !p.event);
  if (!room) return space;
  const forRoom = space.filter((p) => p.room === room);
  const shared = space.filter((p) => !p.room || p.room === 'all');
  if (forRoom.length) return [...forRoom, ...shared];
  return shared.length ? shared : space;
}

export function workPhotos(photos) {
  return (photos || []).filter((p) => p.event && p.event !== 'about');
}

export function aboutPhotos(photos) {
  return (photos || []).filter((p) => p.event === 'about');
}

export function workTags(photos) {
  const seen = [];
  for (const p of workPhotos(photos)) {
    if (p.event && !seen.includes(p.event)) seen.push(p.event);
  }
  return seen;
}
